package com.example.readingnotes.data.remote.dto

import kotlinx.serialization.Serializable

/**
 * Visibility levels for posts
 */
@Serializable
enum class VisibilityLevel {
    GLOBAL, // Visible to everyone (authenticated and anonymous)
    PUBLIC, // Visible to all authenticated users
    TENANT, // Visible to members of the same tenant
    CIRCLE, // Visible to members of specified circles
    PRIVATE, // Visible only to the author
}

@Serializable
data class PostDto(
    val id: String, // UUID
    val title: String,
    val contentMarkdown: String? = null,
    val contentHtml: String? = null,
    val content: String? = null, // Deprecated: for backwards compatibility
    val imageUrl: String? = null, // Deprecated: use images instead
    val imageIds: List<String> = emptyList(), // UUIDs
    val images: List<PostImageDto>? = null, // Only in detail response
    val tags: List<TagDto>? = null, // Only in detail response
    val status: String? = null,
    val createdAt: String,
    val updatedAt: String,
    // Multi-tenant fields
    val tenantId: String? = null, // UUID
    val authorId: String, // UUID
    val visibility: VisibilityLevel? = null,
    val circleIds: List<String> = emptyList(), // UUIDs
    val author: AuthorDto? = null,
    // Paid content fields
    val isPurchasable: Boolean = false,
    val price: Int? = null, // Price in currency units (e.g., JPY)
    val currency: String? = null, // e.g., "JPY", "USD"
    val isPurchased: Boolean = false,
    val hasAccess: Boolean = true,
    val previewHtml: String? = null, // Preview content (first 20%) for unpaid users
    val fullHtml: String? = null, // Full content (same as contentHtml, for clarity)
)

@Serializable
data class AuthorDto(
    val id: String, // UUID
    val username: String? = null,
)

@Serializable
data class CreatePostRequestDto(
    val title: String,
    val contentMarkdown: String,
    val content: String? = null, // Deprecated: for backwards compatibility
    val imageUrl: String? = null, // Deprecated: use imageIds instead
    val imageIds: List<String>? = null, // UUIDs
    // Multi-tenant fields
    val tenantId: String? = null, // Optional, defaults to user's active tenant
    val visibility: VisibilityLevel? = null,
    val circleIds: List<String>? = null, // UUIDs
)

@Serializable
data class UpdatePostRequestDto(
    val title: String? = null,
    val contentMarkdown: String? = null,
    val content: String? = null, // Deprecated: for backwards compatibility
    val imageUrl: String? = null, // Deprecated: use imageIds instead
    val imageIds: List<String>? = null, // UUIDs
    val status: String? = null,
    // Multi-tenant fields
    val visibility: VisibilityLevel? = null,
    val circleIds: List<String>? = null, // UUIDs
)

@Serializable
data class UpdatePostStatusRequestDto(
    val status: String,
)

@Serializable
data class DeleteResponseDto(
    val ok: Boolean,
)

@Serializable
data class BulkDeleteRequestDto(
    val ids: List<String>, // UUIDs
)

@Serializable
data class BulkDeleteResponseDto(
    val ok: Boolean,
    val deletedCount: Int,
)

@Serializable
data class SearchResponseDto(
    val results: List<PostDto>,
    val pagination: SearchPaginationDto,
    val query: String,
)

@Serializable
data class SearchPaginationDto(
    val page: Int,
    val pageSize: Int,
    val total: Int,
    val totalPages: Int,
)

@Serializable
data class SearchSuggestionsResponseDto(
    val suggestions: List<String>,
)
